import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Api, { FailedRequest } from '@common/services/api';
import { useAppSelector } from '@common/hooks';
import * as Alert from '@common/services/alert';
import { EFilters } from '@modules/forms/constants';
import { ApiResponse } from '@common/services/api/types';
import { ShortcutTypes } from '@modules/admin/definitions';

import ModalContent from '@common/components/modal/modal-content';
import ModalWindow from '@common/components/modal/modal-window';
import { AsyncDataProps, AsyncList } from '@common/components/list';
import SearchBar from '@common/components/search-bar';
import Placeholder from '@common/components/placeholder';
import ListShowMoreComponent from '@common/components/list-show-more';
import FormListItem, { FormShortcut } from './form-list-item';

import './add-form-shortcut.scss';

type Filter = {
  q: string | null;
};

type OwnProps = {
  onAdd?: (newShortcut: any) => void;
  onClose?: () => void;
  show: boolean;
};

const AddFormShortcut = ({ onAdd, onClose, show }: OwnProps) => {
  const { t } = useTranslation();
  const orgId = useAppSelector((state) => state.organisation.selected.id);

  const [selectedForm, setSelectedForm] = useState<FormShortcut | null>(null);
  const [search, setSearch] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onHide = useCallback(() => {
    setSearch('');
    setSelectedForm(null);
    setLoading(false);
    if (onClose) onClose();
  }, [onClose, setSearch, setLoading, setSelectedForm]);

  const onConfirm = useCallback(async () => {
    setLoading(true);

    const payload = {
      type: ShortcutTypes.FORM,
      target_id: selectedForm?.id,
    };

    try {
      const { data } = await Api.post<ApiResponse<FormShortcut>>(`/v3/organisations/${orgId}/shortcuts`, payload);
      if (onAdd) onAdd(data);
      Alert.success(t('organisation:forms_shortcut_create_success', {
        name: data.name,
      }));
      onHide();
    } catch (e) {
      Alert.forStatus((e as FailedRequest).status_code, {
        warning: t('organisation:forms_shortcut_create_warning'),
        error: t('organisation:forms_shortcut_create_error'),
      });
      setLoading(false);
      throw e;
    }
  }, [selectedForm, onHide, onAdd, setLoading, t, orgId]);

  const data: AsyncDataProps<FormShortcut, FormShortcut, Filter> = {
    useCursor: true,
    onFetch: (nextCursor, filter, limit = 20) => {
      const query = Api.utils.toQuery({
        ...filter,
        limit,
        cursor: nextCursor || true,
        status: EFilters.LIVE,
      });
      return Api.get(`/v1/organisations/${orgId}/forms?${query}`);
    },
    filter: {
      q: search,
    }
  };

  return (
    <ModalWindow
      className="AddFormShortcutModal"
      show={show}
      onHide={onHide}
      size="small"
    >
      <ModalContent
        tooltip={t('organisation:settings_shortcuts_add_form_description')}
        title={t('organisation:settings_shortcuts_add_form')}
        onHide={onHide}
        onConfirm={onConfirm}
        confirmButtonDisabled={!selectedForm}
        confirmButtonLoading={loading}
        fullWidthContent
      >
        <SearchBar
          onSearch={setSearch}
          placeholder={t('common:form_filter_search_placeholder')}
        />
        <AsyncList
          data={data}
          renderRow={FormListItem}
          rowProps={{
            selectedForm,
            setSelectedForm,
          }}
          placeholder={<Placeholder title={t('forms:no_forms_placeholder')} />}
          ShowMoreComponent={ListShowMoreComponent}
        />
      </ModalContent>
    </ModalWindow>
  );
};

export default AddFormShortcut;
